import type { CSSProperties } from 'react';
import { useSiteOpcional } from '../../context/Info';
import { dataDaCotacao, formatarPreco, temConversorMoeda, useEstadoMoeda } from '../../../utils/moedaSite';

// Preço de imóvel na moeda do idioma (utils/moedaSite). Só é montado nos tenants com o seletor de
// idiomas ligado (utils/idiomaMoedaSite); os demais seguem com o texto de sempre.
// `original` = o texto que o ponto de chamada já mostrava — em real (ou só com o seletor, sem o
// conversor) ele sai igual.
// `notranslate`: o tradutor (GTranslate) troca o nó de texto por <font> e o React passaria a
// atualizar um nó solto — a troca de moeda com a página em inglês não apareceria.
// `detalhe`: na ficha, uma linha pequena abaixo do convertido com o valor oficial e a hora da cotação.
const ESTILO_DETALHE: CSSProperties = { display: 'block', fontSize: '0.72em', fontWeight: 400, lineHeight: 1.3, opacity: 0.8 };

const PrecoMoeda: React.FC<{
    valor: number;
    simbolo: string | null | undefined;
    original: string;
    site?: any;
    detalhe?: boolean;
}> = ({ valor, simbolo, original, site: siteProp, detalhe }) => {
    const siteContexto = useSiteOpcional();
    const converte = temConversorMoeda(siteProp ?? siteContexto);
    const est = useEstadoMoeda(true, converte);
    const texto = converte ? formatarPreco(valor, simbolo, original, est) : original;
    const convertido = texto !== original;
    const quando = convertido ? dataDaCotacao(est) : '';
    return (
        <span className="notranslate" translate="no" title={convertido ? `${original} · cotação de ${quando}` : undefined}>
            {texto}
            {convertido && detalhe ? <small style={ESTILO_DETALHE}>{`≈ convertido de ${original} · cotação ${quando}`}</small> : null}
        </span>
    );
};

/**
 * Ponto de preço dos cards/fichas: fora do gate (`ativo=false`) devolve a MESMA string de sempre —
 * `prefixo + original`, um só nó de texto, byte a byte (SSR idêntico nos ~300 sites). No gate, o
 * <PrecoMoeda> (span `notranslate`, convertido quando o conversor está ligado).
 */
export const precoOuTexto = (
    ativo: boolean,
    valor: number,
    simbolo: string | null | undefined,
    original: string,
    prefixo = '',
    detalhe = false,
    sufixo = ''
): React.ReactNode =>
    ativo ? (
        <>
            {prefixo}
            <PrecoMoeda valor={valor} simbolo={simbolo} original={original} detalhe={detalhe} />
            {sufixo}
        </>
    ) : (
        `${prefixo}${original}${sufixo}`
    );

/**
 * Texto de preço que já vem pronto de um helper (precosRotulados, precoDoImovel): no gate embrulha
 * em `notranslate` (o tradutor não pode trocar o nó que a moeda atualiza); fora dele, o texto puro.
 */
export const textoNotranslate = (ativo: boolean, texto: string): React.ReactNode =>
    ativo ? <span className="notranslate" translate="no">{texto}</span> : texto;

export default PrecoMoeda;
